import { Controller } from "@hotwired/stimulus"
import { clockLabel, durationLabel, pad } from "@/utils/time_tracking_format"

// Google Calendar-style drag-to-create on the day timeline: press on empty
// canvas and sweep out a range -- the ghost grows in snap-minute steps --
// and releasing opens the manual-entry modal prefilled with that start
// time and duration. This is the only way to backfill from the timeline.
// Presses on a session block are ignored (blocks select their session),
// and a press that never moves does nothing, so a stray click inside the
// panel can't pop the modal.
export default class extends Controller {
  static targets = ["ghost", "ghostLabel"]
  static values = {
    hourPx: Number,
    snapMinutes: { type: Number, default: 15 },
    date: String,
    createPath: String,
    frame: { type: String, default: "modal" },
    threshold: { type: Number, default: 4 }
  }

  connect() {
    this.onMove = this.onMove.bind(this)
    this.onUp = this.onUp.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
  }

  disconnect() {
    this.stopListening()
  }

  start(event) {
    if (event.button !== 0) return
    if (event.target.closest(".time-tracking-block")) return

    this.originY = this.offsetY(event)
    this.currentY = this.originY
    this.dragging = false
    window.addEventListener("mousemove", this.onMove)
    window.addEventListener("mouseup", this.onUp)
    window.addEventListener("keydown", this.onKeydown)
  }

  onMove(event) {
    this.currentY = this.offsetY(event)
    if (!this.dragging && Math.abs(this.currentY - this.originY) < this.thresholdValue) return

    // Only claim the gesture once it's clearly a drag, so plain clicks
    // still reach the gap prompts underneath.
    this.dragging = true
    event.preventDefault()
    this.element.classList.add("user-select-none")
    this.drawGhost()
  }

  onUp(event) {
    const dragged = this.dragging
    this.cancel()
    if (!dragged) return

    event.preventDefault()
    this.suppressNextClick()
    this.openModal()
  }

  onKeydown(event) {
    if (event.key === "Escape") this.cancel()
  }

  cancel() {
    this.dragging = false
    this.stopListening()
    this.element.classList.remove("user-select-none")
    if (this.hasGhostTarget) this.ghostTarget.classList.add("d-none")
  }

  stopListening() {
    window.removeEventListener("mousemove", this.onMove)
    window.removeEventListener("mouseup", this.onUp)
    window.removeEventListener("keydown", this.onKeydown)
  }

  drawGhost() {
    if (!this.hasGhostTarget) return

    const { startMinutes, minutes } = this.range()
    this.ghostTarget.classList.remove("d-none")
    this.ghostTarget.style.top = `${startMinutes * this.pxPerMinute}px`
    this.ghostTarget.style.height = `${minutes * this.pxPerMinute}px`
    if (this.hasGhostLabelTarget) {
      this.ghostLabelTarget.textContent = `${clockLabel(startMinutes)} · ${durationLabel(minutes)}`
    }
  }

  openModal() {
    const { startMinutes, minutes } = this.range()
    const startedAt = `${this.dateValue}T${pad(Math.floor(startMinutes / 60))}:${pad(startMinutes % 60)}`
    const url = `${this.createPathValue}?started_at=${encodeURIComponent(startedAt)}&duration_minutes=${minutes}`
    const frame = document.getElementById(this.frameValue)
    if (frame) frame.src = url
  }

  // The swept pixels as minutes from midnight, snapped outward so the
  // range always covers what was dragged and never falls below one
  // increment.
  range() {
    const snap = this.snapMinutesValue
    const dayMinutes = 24 * 60
    const top = Math.max(0, Math.min(this.originY, this.currentY))
    const bottom = Math.max(this.originY, this.currentY)

    const startMinutes = Math.min(Math.floor(top / this.pxPerMinute / snap) * snap, dayMinutes - snap)
    const endMinutes = Math.min(
      Math.max(Math.ceil(bottom / this.pxPerMinute / snap) * snap, startMinutes + snap),
      dayMinutes
    )
    return { startMinutes, minutes: endMinutes - startMinutes }
  }

  get pxPerMinute() {
    return this.hourPxValue / 60
  }

  offsetY(event) {
    return event.clientY - this.element.getBoundingClientRect().top
  }

  // The mouseup that ends a drag is followed by a click; swallow it so it
  // can't also trigger whatever sits under the cursor.
  suppressNextClick() {
    const swallow = (event) => {
      event.preventDefault()
      event.stopPropagation()
    }
    window.addEventListener("click", swallow, { capture: true, once: true })
  }
}
